import {
  usePortalContent,
  useAccountPortal,
  useDocumentsPortal,
  getPortalIcon,
  getAccountSettingsMeta,
  buildAccountNavItems,
  getDocumentsHelpSection,
} from '../../portal';
import PageHeader from '../../components/PageHeader';

const STEP_GRADIENTS = {
  dark: 'from-dark-700 to-dark-800',
  primary: 'from-primary-600 to-primary-700',
  emerald: 'from-emerald-600 to-emerald-700',
  blue: 'from-blue-600 to-blue-700',
};

const DOC_CATEGORY_STYLES = {
  primary: {
    wrap: 'bg-primary-50 dark:bg-primary-900/20 border-primary-100 dark:border-primary-800/30',
    bullet: 'text-primary-500',
  },
  secondary: {
    wrap: 'bg-secondary-50 dark:bg-secondary-900/20 border-secondary-100 dark:border-secondary-800/30',
    bullet: 'text-secondary-500',
  },
  emerald: {
    wrap: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800/30',
    bullet: 'text-emerald-500',
  },
  blue: {
    wrap: 'bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800/30',
    bullet: 'text-blue-500',
  },
};

const CONTACT_STYLES = {
  primary: {
    iconWrap: 'bg-primary-100 dark:bg-primary-900/30',
    icon: 'text-primary-600 dark:text-primary-400',
    hover: 'hover:border-primary-300 dark:hover:border-primary-600',
  },
  green: {
    iconWrap: 'bg-green-100 dark:bg-green-900/30',
    icon: 'text-green-600 dark:text-green-400',
    hover: 'hover:border-green-300 dark:hover:border-green-600',
  },
  blue: {
    iconWrap: 'bg-blue-100 dark:bg-blue-900/30',
    icon: 'text-blue-600 dark:text-blue-400',
    hover: 'hover:border-blue-300 dark:hover:border-blue-600',
  },
};

const SectionHeader = ({ iconName, title, large = false }) => (
  <div className="flex items-center gap-3 mb-4">
    <div
      className={`${large ? 'w-12 h-12' : 'w-10 h-10'} rounded-xl flex items-center justify-center shadow-lg transition-transform duration-300 hover:scale-110 hover:rotate-6`}
      style={{
        background: 'linear-gradient(135deg, var(--primary-500), var(--primary-600))',
        boxShadow: large ? '0 10px 15px -3px var(--glow-color)' : undefined,
      }}
    >
      {(() => {
        const Icon = getPortalIcon(iconName);
        return <Icon className={`${large ? 'w-6 h-6' : 'w-5 h-5'} text-white`} />;
      })()}
    </div>
    <h2 className="section-title">{title}</h2>
  </div>
);

const HelpGuide = () => {
  const { content } = usePortalContent('help');
  const { settings, profile, invoice, paymentHistory } = useAccountPortal();
  const { helpSection: documentsHelpContent } = useDocumentsPortal();

  const welcomePrefix = content?.welcomeTitlePrefix ?? 'Welcome to the ';
  const brandHighlight = content?.brandHighlight ?? 'BooksComply';
  const welcomeSuffix = content?.welcomeTitleSuffix ?? ' Help Portal!';
  const guide = content?.helpGuideSection || {};
  const accountMeta = getAccountSettingsMeta(settings);
  const accountItems = buildAccountNavItems(profile, invoice, paymentHistory);
  const documents = getDocumentsHelpSection(documentsHelpContent);
  const pricing = content?.pricingSection || {};
  const tax = content?.taxFilingSection || {};
  const support = content?.supportSection || {};
  const docCategories = documents.categories || [];
  const services = (pricing.services || []).sort((a, b) => (a.order || 0) - (b.order || 0));
  const steps = (tax.steps || []).sort((a, b) => (a.order || 0) - (b.order || 0));
  const contacts = (support.contacts || []).filter((item) => item.isActive !== false).sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="space-y-6">
      <PageHeader
        icon={getPortalIcon(content?.welcomeIcon || 'LuCircleHelp')}
        title={
          <>
            {welcomePrefix}
            <span style={{ color: 'var(--primary-500)' }}>{brandHighlight}</span>
            {welcomeSuffix}
          </>
        }
        subtitle={content?.welcomeSubtitle || 'This guide walks you through the key features and functionalities available to help you navigate and make the most of our platform.'}
      />

      <div className="card p-6 opacity-0 animate-fade-in-up hover:shadow-xl transition-shadow duration-300" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
        <SectionHeader iconName={guide.icon || 'FaQuestionCircle'} title={guide.title || 'Help Guide'} large />
        <p className="body-text mb-6">
          {guide.description || 'Explore how the portal works in detail through our Help Guide. Find answers, instructions, and tips to use every section efficiently.'}
        </p>
      </div>

      {accountItems.length > 0 && (
        <div className="card p-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
          <SectionHeader iconName={accountMeta.icon || 'FaCog'} title={accountMeta.title || 'Account Settings'} large />
          {accountMeta.intro && <p className="body-text mb-6">{accountMeta.intro}</p>}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {accountItems.map((item) => {
              const Icon = getPortalIcon(item.icon || 'FaUser');
              return (
                <div
                  key={item.key}
                  className="p-4 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg group cursor-pointer"
                  style={{ borderColor: 'transparent' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--primary-300)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; }}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-110" style={{ background: 'var(--primary-100)' }}>
                      <Icon className="w-5 h-5" style={{ color: 'var(--primary-600)' }} />
                    </div>
                    <h3 className="font-semibold text-dark-800 dark:text-white">{item.title}</h3>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{item.helpDescription}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {docCategories.length > 0 && (
        <div className="card p-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}>
          <SectionHeader iconName={documents.icon || 'FaFolder'} title={documents.title || 'Documents'} large />
          {documents.intro && <p className="text-gray-600 dark:text-gray-400 mb-6">{documents.intro}</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {docCategories.map((cat, index) => {
              const styles = DOC_CATEGORY_STYLES[cat.color] || DOC_CATEGORY_STYLES.primary;
              return (
                <div key={`doc-cat-${index}`} className={`p-5 rounded-xl border ${styles.wrap}`}>
                  <h3 className="font-bold text-dark-800 dark:text-white mb-2">{cat.title}</h3>
                  <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    {(cat.bullets || []).map((bullet, bi) => (
                      <li key={`bullet-${bi}`} className="flex items-start gap-2">
                        <span className={`${styles.bullet} mt-1`}>•</span>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {services.length > 0 && (
        <div className="card p-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '400ms', animationFillMode: 'forwards' }}>
          <h2 className="section-title mb-6">{pricing.title || 'Pricing Plan: Services You Can Use'}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {services.map((service, index) => {
              const Icon = getPortalIcon(service.icon || 'FaLock');
              return (
                <div
                  key={`service-${index}`}
                  className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                    service.active
                      ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
                      : 'border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className={`w-5 h-5 ${service.active ? 'text-green-500' : 'text-gray-400 dark:text-gray-500'}`} />
                    <div>
                      <p className={`font-semibold ${service.active ? 'text-green-700 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>{service.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{service.status}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {steps.length > 0 && (
        <div className="card p-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '500ms', animationFillMode: 'forwards' }}>
          <h2 className="section-title mb-3">{tax.title || 'Tax Filing Portal: Track Your Tax Return'}</h2>
          {tax.subtitle && <p className="text-gray-600 dark:text-gray-400 mb-6">{tax.subtitle}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {steps.map((step, index) => (
              <div
                key={`step-${index}`}
                className={`p-5 rounded-xl bg-gradient-to-br ${STEP_GRADIENTS[step.color] || STEP_GRADIENTS.primary} text-white transition-all duration-300 hover:scale-[1.05] hover:shadow-2xl hover:-translate-y-2 cursor-pointer group`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold">Step {step.step ?? index + 1}</span>
                  <span className={`px-2 py-1 rounded text-xs font-semibold uppercase ${step.status === 'in-progress' ? 'bg-white/20' : 'bg-white/10'}`}>
                    {step.status === 'in-progress' ? 'In Progress' : 'Pending'}
                  </span>
                </div>
                <h3 className="font-medium text-lg">{step.title}</h3>
                {step.status === 'in-progress' && (
                  <div className="mt-4">
                    <div className="h-1.5 bg-white/30 rounded-full overflow-hidden">
                      <div className="h-full w-0 bg-white rounded-full" />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card p-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '700ms', animationFillMode: 'forwards' }}>
        <h2 className="section-title mb-4">{support.title || 'Support/Questions/Connect with us:'}</h2>
        {support.intro && (
          <p className="text-gray-600 dark:text-gray-400 mb-6">{support.intro}</p>
        )}

        {support.helpTitle && <h3 className="font-bold text-dark-800 dark:text-white mb-4">{support.helpTitle}</h3>}

        {contacts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {contacts.map((contact, index) => {
              const Icon = getPortalIcon(contact.icon || 'FaEnvelope');
              const styles = CONTACT_STYLES[contact.color] || CONTACT_STYLES.primary;
              return (
                <a
                  key={`contact-${index}`}
                  href={contact.url || '#'}
                  target={contact.url?.startsWith('http') ? '_blank' : undefined}
                  rel={contact.url?.startsWith('http') ? 'noopener noreferrer' : undefined}
                  className={`flex items-center gap-4 p-4 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 ${styles.hover} transition-all duration-200`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${styles.iconWrap}`}>
                    <Icon className={`w-5 h-5 ${styles.icon}`} />
                  </div>
                  <div>
                    <p className="font-semibold text-dark-800 dark:text-white">{contact.label}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{contact.value}</p>
                  </div>
                </a>
              );
            })}
          </div>
        )}

        {support.notificationTip && (
          <div className="p-4 rounded-xl bg-secondary-50 dark:bg-secondary-900/20 border border-secondary-100 dark:border-secondary-800/30 flex items-start gap-3">
            {(() => {
              const NotifIcon = getPortalIcon(support.notificationIcon || 'LuBell');
              return <NotifIcon className="w-5 h-5 text-secondary-600 dark:text-secondary-400 shrink-0 mt-0.5" />;
            })()}
            <p className="text-sm text-secondary-700 dark:text-secondary-300">{support.notificationTip}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpGuide;
